import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useCurrency } from "@/hooks/useUserCurrency";
import type { MonthPoint } from "@/lib/finance";

interface PatrimonyEvolutionChartProps {
  data: MonthPoint[];
}

export function PatrimonyEvolutionChart({ data }: PatrimonyEvolutionChartProps) {
  const { country, format } = useCurrency();

  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload as MonthPoint;
    const rows = [
      { label: "Na rua no fim do mês", value: point.outstanding, color: "hsl(var(--primary))" },
      { label: "Lucro acumulado", value: point.cumulativeProfit, color: "hsl(var(--gold))" },
    ];
    return (
      <div className="rounded-xl border border-border bg-popover p-3 text-xs shadow-xl">
        <p className="mb-2 font-semibold text-foreground">{point.label}</p>
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                {r.label}
              </span>
              <span className="tabular font-medium text-foreground">{format(r.value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="glass">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold tracking-tight">Evolução do dinheiro</CardTitle>
        <CardDescription className="text-xs">
          Quanto ficou na rua no fim de cada mês e quanto de lucro você já acumulou
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" vertical={false} opacity={0.5} />
            <XAxis
              dataKey="month"
              stroke="hsl(var(--muted-foreground))"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${country.currencySymbol} ${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip cursor={{ stroke: "hsl(var(--primary) / 0.3)", strokeWidth: 1 }} content={renderTooltip} />
            <Line
              type="monotone"
              dataKey="outstanding"
              name="Na rua"
              stroke="hsl(var(--primary))"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 2 }}
            />
            <Line
              type="monotone"
              dataKey="cumulativeProfit"
              name="Lucro acumulado"
              stroke="hsl(var(--gold))"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, fill: "hsl(var(--gold))", stroke: "hsl(var(--background))", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>

        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "hsl(var(--primary))" }} />
            Na rua (ainda não recuperado)
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "hsl(var(--gold))" }} />
            Lucro acumulado (juros + multas)
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
